import { t } from '../i18n';
// client/admin-client/src/components/InfoPresetBuilder.jsx
import React, { useMemo, useState } from 'react';
import axios from '../api/axios';
import InfoCardDesignEditor from './InfoCardDesignEditor';
import { createDefaultInfoCardDesign, normalizeInfoCardDesign } from './infoCardDesignDefaults';
export function defaultInfoDraft() {
    return {
        highlight_point: '',
        highlight_message: '',
        status: 'active', // active / scheduled / expired / disabled
        time_mode: 'infinite', // infinite | range
        start_local: '',
        end_local: '',
        refresh_days: 30,
        // Information Button targeting: all/ALL 기본값이면 기존 버튼처럼 전체 노출
        device: 'all', // all | desktop | mobile
        language: 'all', // all | ko | en | ja | zh | ru | th | vi ...
        country: 'ALL', // ALL | KR | US | JP | CN | RU | TH | VN ...
        priority: 0,
        // 같은 이벤트의 desktop/mobile/language 변형을 묶는 영구 family key (preset.targeting.familyKey)
        family_key: '',
        // InfoCard Timer: DisplaySet.time(노출 기간)과 별개로 preset 문자열에 저장
        timer_mode: 'none', // none | daily_reset | due_date
        timer_end_local: '',
        bg_mode: 'color', // color | image
        bg_color: '#000000',
        bg_image_url: '',
        url: '',
        textFontFamily: 'inherit',
        textFontWeight: '700',
        textColor: '#111111',
        textFontSize: 13,
        elements: [{ type: 'text', value: '' }], // text | timer | icon
        // 광고 편집기와 동일한 자유 배치형 이벤트 카드 디자인
        design_enabled: false,
        design_template_key: 'FREEFORM_V1',
        design_background_mode: 'COVER',
        design_image_url: '',
        design_desktop_image_url: '',
        design_mobile_image_url: '',
        design_content_json: createDefaultInfoCardDesign().contentJson,
        design_layout_json: createDefaultInfoCardDesign().layoutJson,
    };
}
// datetime-local(string) -> UTC ISO (Z)
export function localToUtcIso(localStr) {
    if (!localStr)
        return null;
    const d = new Date(localStr);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString();
}
export function utcIsoToLocalInput(iso) {
    if (!iso)
        return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '';
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function normalizeInfoTimerMode(v) {
    const s = String(v || 'none').trim().toLowerCase();
    return ['none', 'daily_reset', 'due_date'].includes(s) ? s : 'none';
}
export function validateInfoTimerDraft(draft) {
    const timerMode = normalizeInfoTimerMode(draft?.timer_mode || draft?.timerMode);
    if (timerMode !== 'due_date')
        return { ok: true };
    const raw = draft?.timer_end_local || draft?.timerEndLocal || draft?.timer_end_utc || draft?.timerEndUtc || '';
    if (!raw)
        return { ok: false, message: t("Due Date\uB97C \uC785\uB825\uD574\uC57C \uD569\uB2C8\uB2E4.") };
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()))
        return { ok: false, message: t("Due Date\uB97C \uC62C\uBC14\uB978 \uB0A0\uC9DC/\uC2DC\uAC04\uC73C\uB85C \uC785\uB825\uD574\uC57C \uD569\uB2C8\uB2E4.") };
    return { ok: true };
}
export function buildTimePayload(draft) {
    if (draft.time_mode === 'range') {
        const s = localToUtcIso(draft.start_local);
        const e = localToUtcIso(draft.end_local);
        if (!s || !e)
            return null;
        return { mode: 'range', start_utc: s, end_utc: e };
    }
    const rd = Number(draft.refresh_days);
    return { mode: 'infinite', refresh_days: Number.isFinite(rd) && rd > 0 ? rd : 30 };
}
const INFO_TEXT_STYLE_DEFAULTS = {
    textFontFamily: 'inherit',
    textFontWeight: '700',
    textColor: '#111111',
    textFontSize: 13,
};
function clampTextFontSize(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return INFO_TEXT_STYLE_DEFAULTS.textFontSize;
    return Math.min(32, Math.max(10, Math.round(n)));
}
function normalizeTextFontWeight(v) {
    const s = String(v ?? INFO_TEXT_STYLE_DEFAULTS.textFontWeight).trim();
    return ['400', '500', '600', '700', '800'].includes(s) ? s : INFO_TEXT_STYLE_DEFAULTS.textFontWeight;
}
function normalizeTextFontFamily(v) {
    const s = String(v ?? INFO_TEXT_STYLE_DEFAULTS.textFontFamily).trim();
    return s || INFO_TEXT_STYLE_DEFAULTS.textFontFamily;
}
function normalizeTextColor(v) {
    const s = String(v ?? INFO_TEXT_STYLE_DEFAULTS.textColor).trim();
    return /^#[0-9a-fA-F]{6}$/.test(s) ? s : INFO_TEXT_STYLE_DEFAULTS.textColor;
}
function normalizeInfoFamilyKey(v) {
    return String(v || '').trim().replace(/[^a-zA-Z0-9:_-]/g, '').slice(0, 180);
}
// ✅ legacy preset raw + 새 이벤트 카드 디자인(JSONB)을 함께 저장
function buildLegacyPresetRaw(draft) {
    const segs = [];
    if (draft.bg_mode === 'color' && draft.bg_color)
        segs.push(`bgColor=${encodeURIComponent(draft.bg_color)}`);
    if (draft.bg_mode === 'image' && draft.bg_image_url)
        segs.push(`bgImage=${encodeURIComponent(draft.bg_image_url)}`);
    segs.push(`layout=${encodeURIComponent('left_to_right')}`);
    const timerMode = normalizeInfoTimerMode(draft.timer_mode || draft.timerMode);
    segs.push(`timerMode=${encodeURIComponent(timerMode)}`);
    if (timerMode === 'due_date') {
        const timerEndUtc = localToUtcIso(draft.timer_end_local || draft.timerEndLocal || draft.timer_end_utc || draft.timerEndUtc || '');
        if (timerEndUtc)
            segs.push(`timerEndUtc=${encodeURIComponent(timerEndUtc)}`);
    }
    if (draft.url)
        segs.push(`url=${encodeURIComponent(draft.url)}`);
    segs.push(`textFontFamily=${encodeURIComponent(normalizeTextFontFamily(draft.textFontFamily))}`);
    segs.push(`textFontWeight=${encodeURIComponent(normalizeTextFontWeight(draft.textFontWeight))}`);
    segs.push(`textColor=${encodeURIComponent(normalizeTextColor(draft.textColor))}`);
    segs.push(`textFontSize=${encodeURIComponent(String(clampTextFontSize(draft.textFontSize)))}`);
    const els = Array.isArray(draft.elements) ? draft.elements.slice(0, 4) : [];
    els.forEach((el, idx) => {
        const type = String(el?.type || 'text');
        const value = type === 'timer' ? '' : String(el?.value || '');
        segs.push(`e${idx}=${type}:${encodeURIComponent(value)}`);
    });
    return segs.join(',');
}

export function buildPresetRaw(draft) {
    const raw = buildLegacyPresetRaw(draft);
    const familyKey = normalizeInfoFamilyKey(draft?.family_key ?? draft?.familyKey);
    const preset = { raw };
    if (familyKey) preset.targeting = { familyKey };

    if (draft?.design_enabled === false) {
        // family key가 없는 완전한 구형 데이터는 예전 문자열 형식을 그대로 유지한다.
        return familyKey ? preset : raw;
    }

    const design = normalizeInfoCardDesign({
        enabled: draft?.design_enabled,
        templateKey: draft?.design_template_key,
        backgroundMode: draft?.design_background_mode,
        imageUrl: draft?.design_image_url,
        desktopImageUrl: draft?.design_desktop_image_url,
        mobileImageUrl: draft?.design_mobile_image_url,
        contentJson: draft?.design_content_json,
        layoutJson: draft?.design_layout_json,
    }, draft?.highlight_point, draft?.highlight_message);
    return { ...preset, design };
}
export function parsePresetRaw(preset) {
    let raw = '';
    if (typeof preset === 'string')
        raw = preset;
    else if (preset && typeof preset === 'object') {
        if (typeof preset.raw === 'string')
            raw = preset.raw;
        else
            raw = '';
    }
    const out = {
        bg_mode: 'color',
        bg_color: '#000000',
        bg_image_url: '',
        url: '',
        timer_mode: 'none',
        timer_end_local: '',
        timerEndUtc: '',
        textFontFamily: INFO_TEXT_STYLE_DEFAULTS.textFontFamily,
        textFontWeight: INFO_TEXT_STYLE_DEFAULTS.textFontWeight,
        textColor: INFO_TEXT_STYLE_DEFAULTS.textColor,
        textFontSize: INFO_TEXT_STYLE_DEFAULTS.textFontSize,
        elements: [],
        design_enabled: false,
        design_template_key: 'FREEFORM_V1',
        design_background_mode: 'COVER',
        design_image_url: '',
        design_desktop_image_url: '',
        design_mobile_image_url: '',
        design_content_json: createDefaultInfoCardDesign().contentJson,
        design_layout_json: createDefaultInfoCardDesign().layoutJson,
        family_key: '',
    };
    if (preset && typeof preset === 'object') {
        out.family_key = normalizeInfoFamilyKey(
            preset?.targeting?.familyKey ??
            preset?.targeting?.family_key ??
            preset?.targetFamilyKey ??
            preset?.target_family_key ??
            ''
        );
    }
    if (preset && typeof preset === 'object' && preset.design && typeof preset.design === 'object') {
        const design = normalizeInfoCardDesign(preset.design);
        out.design_enabled = design.enabled;
        out.design_template_key = design.templateKey;
        out.design_background_mode = design.backgroundMode;
        out.design_image_url = design.imageUrl;
        out.design_desktop_image_url = design.desktopImageUrl;
        out.design_mobile_image_url = design.mobileImageUrl;
        out.design_content_json = design.contentJson;
        out.design_layout_json = design.layoutJson;
    }
    if (!raw)
        return out;
    const parts = raw.split(',').map((x) => x.trim()).filter(Boolean);
    for (const p of parts) {
        const [k, ...rest] = p.split('=');
        const vv = rest.join('=');
        if (!k)
            continue;
        if (k === 'bgColor') {
            out.bg_mode = 'color';
            out.bg_color = decodeURIComponent(vv || '');
        }
        else if (k === 'bgImage') {
            out.bg_mode = 'image';
            out.bg_image_url = decodeURIComponent(vv || '');
        }
        else if (k === 'url') {
            out.url = decodeURIComponent(vv || '');
        }
        else if (k === 'timerMode') {
            out.timer_mode = normalizeInfoTimerMode(decodeURIComponent(vv || ''));
        }
        else if (k === 'timerEndUtc') {
            const iso = decodeURIComponent(vv || '');
            out.timerEndUtc = iso;
            out.timer_end_local = utcIsoToLocalInput(iso);
        }
        else if (k === 'textFontFamily') {
            out.textFontFamily = normalizeTextFontFamily(decodeURIComponent(vv || ''));
        }
        else if (k === 'textFontWeight') {
            out.textFontWeight = normalizeTextFontWeight(decodeURIComponent(vv || ''));
        }
        else if (k === 'textColor') {
            out.textColor = normalizeTextColor(decodeURIComponent(vv || ''));
        }
        else if (k === 'textFontSize') {
            out.textFontSize = clampTextFontSize(decodeURIComponent(vv || ''));
        }
        else if (k.startsWith('e')) {
            const s = vv || '';
            const i = s.indexOf(':');
            if (i >= 0) {
                const t = s.slice(0, i);
                const v = decodeURIComponent(s.slice(i + 1));
                out.elements.push({ type: t, value: v });
            }
        }
    }
    out.timer_mode = normalizeInfoTimerMode(out.timer_mode);
    if (out.timerEndUtc && !out.timer_end_local)
        out.timer_end_local = utcIsoToLocalInput(out.timerEndUtc);
    out.elements = out.elements.slice(0, 4);
    if (!out.elements.length)
        out.elements = [{ type: 'text', value: '' }];
    return out;
}
export function formatTimeHuman(timeObj) {
    if (!timeObj)
        return '-';
    if (timeObj.mode === 'infinite')
        return t('무한(약 {days}일 단위 갱신)', { days: timeObj.refresh_days ?? 30 });
    if (timeObj.mode === 'range') {
        const s = timeObj.start_utc ? new Date(timeObj.start_utc) : null;
        const e = timeObj.end_utc ? new Date(timeObj.end_utc) : null;
        const sTxt = s && !Number.isNaN(s.getTime()) ? s.toLocaleString() : '-';
        const eTxt = e && !Number.isNaN(e.getTime()) ? e.toLocaleString() : '-';
        return `${sTxt} ~ ${eTxt}`;
    }
    return '-';
}
export function isActiveAt(timeObj, atDate) {
    if (!timeObj)
        return true;
    if (!atDate || Number.isNaN(atDate.getTime()))
        return true;
    if (timeObj.mode === 'infinite')
        return true;
    if (timeObj.mode === 'range') {
        const s = timeObj.start_utc ? new Date(timeObj.start_utc) : null;
        const e = timeObj.end_utc ? new Date(timeObj.end_utc) : null;
        if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
            return true;
        return atDate.getTime() >= s.getTime() && atDate.getTime() <= e.getTime();
    }
    return true;
}
export const TARGET_LANGUAGE_OPTIONS = [
    { value: 'all', label: t('all / 전체 언어') },
    { value: 'ko', label: t('ko / 한국어') },
    { value: 'en', label: 'en / English' },
    { value: 'ja', label: 'ja / 日本語' },
    { value: 'zh', label: 'zh / 中文' },
    { value: 'ru', label: 'ru / Русский' },
    { value: 'th', label: 'th / ไทย' },
    { value: 'vi', label: 'vi / Tiếng Việt' },
    { value: 'es', label: 'es / Español' },
    { value: 'fr', label: 'fr / Français' },
    { value: 'de', label: 'de / Deutsch' },
    { value: 'tr', label: 'tr / Türkçe' },
    { value: 'pt', label: 'pt / Português' },
];
export const TARGET_DEVICE_OPTIONS = [
    { value: 'all', label: t('all / 데스크톱+모바일') },
    { value: 'desktop', label: t('desktop / 데스크톱') },
    { value: 'mobile', label: t('mobile / 모바일') },
];
export function normalizeInfoTargetLanguage(v) {
    const s = String(v || 'all').trim().replace(/_/g, '-').toLowerCase();
    if (!s || s === 'all' || s === '*')
        return 'all';
    if (s === 'kr')
        return 'ko';
    return s.split('-')[0] || 'all';
}
export function normalizeInfoTargetCountry(v) {
    const s = String(v || 'ALL').trim().toUpperCase();
    if (!s || s === 'ALL' || s === '*')
        return 'ALL';
    return s.replace(/[^A-Z0-9_-]/g, '').slice(0, 16) || 'ALL';
}
export function normalizeInfoTargetDevice(v) {
    const s = String(v || 'all').trim().toLowerCase();
    if (s === 'desktop' || s === 'pc' || s === 'web')
        return 'desktop';
    if (s === 'mobile' || s === 'm')
        return 'mobile';
    return 'all';
}
export function normalizeInfoPriority(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return 0;
    return Math.max(-9999, Math.min(9999, Math.round(n)));
}
export function buildInfoTargetPayload(draft = {}) {
    return {
        device: normalizeInfoTargetDevice(draft.device ?? draft.target_device ?? draft.targetDevice),
        language: normalizeInfoTargetLanguage(draft.language ?? draft.lang ?? draft.locale),
        // 현재 프로젝트는 회원 국가 판별 없이 UI 언어 기준으로만 인포 버튼을 분기한다.
        // 국가값은 표시 제외/ALL 고정해서 언어별 설정이 국가 필터에 막히지 않게 한다.
        country: 'ALL',
        priority: normalizeInfoPriority(draft.priority),
    };
}
// [ADD][ANCHOR:ADMIN_AUTH_HEADERS_LOCAL]
function getAuthHeaders() {
    const token = localStorage.getItem('token') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
}
function isLikelyUrl(v) {
    const s = String(v || '');
    return s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/uploads/') || s.startsWith('/ad-uploads/');
}
function resolveAdminPreviewUrl(rawValue) {
    const value = String(rawValue || '').trim().replace(/\\/g, '/');
    if (!value)
        return '';
    if (/^(https?:\/\/|data:|blob:)/i.test(value))
        return value;
    const base = String(axios?.defaults?.baseURL || '').replace(/\/$/, '');
    if (value.startsWith('/'))
        return base ? `${base}${value}` : value;
    return base ? `${base}/${value}` : value;
}
export default function InfoPresetBuilder({ value, onChange, title = 'Information Button', allowRemove = false, onRemove, enableAdvancedDesign = false, }) {
    const presetRaw = useMemo(() => buildPresetRaw(value), [value]);
    const presetPreview = useMemo(() => typeof presetRaw === 'string' ? presetRaw : JSON.stringify(presetRaw, null, 2), [presetRaw]);
    const canAddElement = (value.elements?.length || 0) < 4;
    // [ADD][ANCHOR:ICON_UPLOAD_STATE]
    const [uploadingIdx, setUploadingIdx] = useState(-1);
    const [uploadingBg, setUploadingBg] = useState(false);
    const [uploadError, setUploadError] = useState('');
    function setField(k, v) {
        onChange({ ...value, [k]: v });
    }
    function setElement(idx, patch) {
        const nextEls = (value.elements || []).map((e, i) => (i === idx ? { ...e, ...patch } : e));
        onChange({ ...value, elements: nextEls });
    }
    function addElement(type) {
        const els = Array.isArray(value.elements) ? value.elements.slice(0, 4) : [];
        if (els.length >= 4)
            return;
        // [PATCH][ANCHOR:TIMER_EXIST_DEFAULT]
        // timer는 값 없이 존재만
        if (type === 'timer')
            els.push({ type, value: '' });
        else
            els.push({ type, value: '' });
        onChange({ ...value, elements: els });
    }
    function removeElement(idx) {
        const els = (value.elements || []).filter((_, i) => i !== idx);
        onChange({ ...value, elements: els.length ? els : [{ type: 'text', value: '' }] });
    }
    // [ADD][ANCHOR:UPLOAD_ICON_HANDLER]
    async function uploadIconFile(idx, file) {
        if (!file)
            return;
        setUploadError('');
        setUploadingIdx(idx);
        try {
            // 간단한 용량 가드(기본 1MB)
            const max = 1 * 1024 * 1024;
            if (file.size > max) {
                setUploadError(t("\uC544\uC774\uCF58 \uD30C\uC77C\uC740 1MB \uC774\uD558\uB85C \uC62C\uB824\uC8FC\uC138\uC694."));
                setUploadingIdx(-1);
                return;
            }
            const dataUrl = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(String(r.result || ''));
                r.onerror = reject;
                r.readAsDataURL(file);
            });
            const res = await axios.post('/admin/assets/upload-icon', { data_url: dataUrl, filename: file.name }, { headers: getAuthHeaders() });
            const url = res.data?.url;
            if (!url)
                throw new Error('NO_URL');
            setElement(idx, { value: url });
        }
        catch (e) {
            console.error('uploadIconFile error:', e);
            setUploadError(t("\uC544\uC774\uCF58 \uC5C5\uB85C\uB4DC \uC2E4\uD328 (\uC11C\uBC84 \uC5C5\uB85C\uB4DC API \uD655\uC778 \uD544\uC694)"));
        }
        finally {
            setUploadingIdx(-1);
        }
    }
    // [ADD][ANCHOR:UPLOAD_INFO_BACKGROUND_HANDLER]
    async function uploadBackgroundFile(file) {
        if (!file)
            return;
        setUploadError('');
        setUploadingBg(true);
        try {
            const max = 5 * 1024 * 1024;
            if (file.size > max) {
                setUploadError(t("배경 이미지 파일은 5MB 이하로 올려주세요."));
                setUploadingBg(false);
                return;
            }
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post('/admin/assets/upload-info-background', fd, { headers: getAuthHeaders() });
            const url = res.data?.url;
            if (!url)
                throw new Error('NO_URL');
            onChange({ ...value, bg_mode: 'image', bg_image_url: url });
        }
        catch (e) {
            console.error('uploadBackgroundFile error:', e);
            setUploadError(t("배경 이미지 업로드 실패 (서버 업로드 API 확인 필요)"));
        }
        finally {
            setUploadingBg(false);
        }
    }
    return (<div className="border rounded p-3 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold">{title}</div>
        {allowRemove ? (<button type="button" onClick={onRemove} className="px-2 py-1 text-sm rounded border hover:bg-gray-50">{t("\uC0AD\uC81C")}</button>) : null}
      </div>

      {uploadError ? (<div className="p-2 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
          {uploadError}
        </div>) : null}

      <div className="border rounded p-3 bg-blue-50/50 space-y-2">
        <div className="font-medium">{t("노출 타겟 / 개별 배경 기준")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("디바이스")}</span>
            <select className="border rounded px-3 py-2 bg-white" value={normalizeInfoTargetDevice(value.device)} onChange={(e) => setField('device', e.target.value)}>
              {TARGET_DEVICE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("언어")}</span>
            <select className="border rounded px-3 py-2 bg-white" value={normalizeInfoTargetLanguage(value.language)} onChange={(e) => setField('language', e.target.value)}>
              {TARGET_LANGUAGE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("우선순위")}</span>
            <input className="border rounded px-3 py-2 bg-white" type="number" step="1" value={value.priority ?? 0} onChange={(e) => setField('priority', e.target.value)}/>
          </label>
        </div>
        <div className="text-xs text-gray-500">{t("* 이 항목의 배경은 선택한 디바이스/언어 타겟에만 적용됩니다. 국가값은 현재 사용하지 않으므로 저장 시 ALL로 고정됩니다. desktop/mobile 또는 언어별로 다르게 쓰려면 각 타겟 항목을 따로 생성해서 배경을 저장하세요.")}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">{t("\uD558\uC774\uB77C\uC774\uD2B8 \uD3EC\uC778\uD2B8")}</span>
          <input className="border rounded px-3 py-2" value={value.highlight_point || ''} onChange={(e) => setField('highlight_point', e.target.value)} placeholder={t("\uC608: \uD83D\uDD27HOT")}/>
        </label>

        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-sm text-gray-600">{t("\uD558\uC774\uB77C\uC774\uD2B8 \uBA54\uC138\uC9C0")}</span>
          <input className="border rounded px-3 py-2" value={value.highlight_message || ''} onChange={(e) => setField('highlight_message', e.target.value)} placeholder={t("\uC608: \uD83D\uDD25 12/31 \uC774\uBCA4\uD2B8 \uC9C4\uD589\uC911")}/>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">{t("\uC0C1\uD0DC")}</span>
          <select className="border rounded px-3 py-2" value={value.status || 'active'} onChange={(e) => setField('status', e.target.value)}>
            <option value="active">active</option>
            <option value="scheduled">scheduled</option>
            <option value="expired">expired</option>
            <option value="disabled">disabled</option>
          </select>
        </label>

        <div className="md:col-span-2 border rounded p-3">
          <div className="font-medium mb-2">{t("\uC2DC\uAC04 \uC124\uC815")}</div>
          <div className="flex gap-3 items-center flex-wrap">
            <label className="flex items-center gap-2">
              <input type="radio" checked={value.time_mode === 'range'} onChange={() => setField('time_mode', 'range')}/>{t("\uAE30\uAC04")}</label>

            <label className="flex items-center gap-2">
              <input type="radio" checked={value.time_mode === 'infinite'} onChange={() => setField('time_mode', 'infinite')}/>{t("\uBB34\uD55C(30\uC77C \uAC31\uC2E0)")}</label>
          </div>

          {value.time_mode === 'range' ? (<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-600">{t("\uC2DC\uC791(\uB85C\uCEEC)")}</span>
                <input className="border rounded px-3 py-2" type="datetime-local" value={value.start_local || ''} onChange={(e) => setField('start_local', e.target.value)}/>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-600">{t("\uC885\uB8CC(\uB85C\uCEEC)")}</span>
                <input className="border rounded px-3 py-2" type="datetime-local" value={value.end_local || ''} onChange={(e) => setField('end_local', e.target.value)}/>
              </label>
            </div>) : (<div className="mt-3">
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t("\uAC31\uC2E0 \uC8FC\uAE30(\uC77C)")}</span>
                <input className="border rounded px-2 py-1 w-24" type="number" min="1" value={value.refresh_days ?? 30} onChange={(e) => setField('refresh_days', e.target.value)}/>
              </label>
              <div className="text-xs text-gray-500 mt-1">{t("* \uBB34\uD55C \uBAA8\uB4DC\uC77C \uB54C UI\uC0C1 \u201C30\uC77C\uB9C8\uB2E4 \uAC31\uC2E0\u201D\uB85C \uD45C\uC2DC\uC6A9")}</div>
            </div>)}
        </div>
      </div>

      <div className="border rounded p-3 bg-slate-50">
        <div className="font-medium mb-2">{t("\uC778\uD3EC\uCE74\uB4DC \uD0C0\uC774\uBA38")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("\uD0C0\uC774\uBA38 \uD0C0\uC785")}</span>
            <select className="border rounded px-3 py-2 bg-white" value={normalizeInfoTimerMode(value.timer_mode || value.timerMode)} onChange={(e) => setField('timer_mode', e.target.value)}>
              <option value="none">{t("\uD0C0\uC774\uBA38 \uC5C6\uC74C")}</option>
              <option value="daily_reset">{t("24\uC2DC\uAC04 \uBC18\uBCF5")}</option>
              <option value="due_date">{t("Due Date \uC9C0\uC815")}</option>
            </select>
          </label>

          {normalizeInfoTimerMode(value.timer_mode || value.timerMode) === 'due_date' ? (<label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-sm text-gray-600">{t("Due Date(\uAD00\uB9AC\uC790 \uB85C\uCEEC \uC2DC\uAC04)")}</span>
              <input className="border rounded px-3 py-2 bg-white" type="datetime-local" value={value.timer_end_local || ''} onChange={(e) => setField('timer_end_local', e.target.value)}/>
            </label>) : null}
        </div>
        <div className="text-xs text-gray-500 mt-2">{t("* \uC774 \uC124\uC815\uC740 \uC778\uD3EC\uCE74\uB4DC \uB0B4\uBD80 \uCE74\uC6B4\uD2B8\uB2E4\uC6B4 \uAE30\uC900\uC785\uB2C8\uB2E4. \uC704 \uC2DC\uAC04 \uC124\uC815\uC740 \uB85C\uBE44 \uB178\uCD9C \uAE30\uAC04\uB9CC \uC81C\uC5B4\uD569\uB2C8\uB2E4.")}</div>
      </div>

      <div className="border rounded p-3">
        <div className="font-medium mb-2">{t("\uD504\uB9AC\uC14B(\uBC30\uACBD/\uC694\uC18C/URL)")}</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("\uBC30\uACBD \uD0C0\uC785")}</span>
            <select className="border rounded px-3 py-2" value={value.bg_mode || 'color'} onChange={(e) => setField('bg_mode', e.target.value)}>
              <option value="color">{t("\uC0C9\uC0C1")}</option>
              <option value="image">{t("\uC774\uBBF8\uC9C0(URL)")}</option>
            </select>
          </label>

          {value.bg_mode === 'color' ? (<label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uBC30\uACBD \uC0C9\uC0C1")}</span>
              <input className="border rounded px-3 py-2 h-[42px]" type="color" value={value.bg_color || '#000000'} onChange={(e) => setField('bg_color', e.target.value)}/>
            </label>) : (<div className="flex flex-col gap-2 md:col-span-2">
              <span className="text-sm text-gray-600">{t("배경 이미지 업로드 / URL")}</span>
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => uploadBackgroundFile(e.target.files?.[0])} disabled={uploadingBg} className="text-sm md:w-56"/>
                <input className="border rounded px-3 py-2 flex-1" value={value.bg_image_url || ''} onChange={(e) => setField('bg_image_url', e.target.value)} placeholder={t("업로드하면 /uploads/... 로 자동 입력됨 (또는 URL 직접 입력)")} disabled={uploadingBg}/>
              </div>
              <div className="text-xs text-gray-500">{t("* 배경 이미지는 최대 5MB까지 업로드 가능합니다. 기존 URL 직접 입력 방식도 유지됩니다.")}</div>
              {isLikelyUrl(value.bg_image_url) ? (<div className="w-full h-24 rounded border bg-white overflow-hidden">
                  <img src={resolveAdminPreviewUrl(value.bg_image_url)} alt="info background" className="w-full h-full object-cover"/>
                </div>) : null}
            </div>)}

          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-sm text-gray-600">{t("이동 URL")}</span>
            <input className="border rounded px-3 py-2" value={value.url || ''} onChange={(e) => setField('url', e.target.value)} placeholder={t("/events/avatar-reward 또는 https://...")}/>
          </label>

          <div className="md:col-span-3 rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-gray-700">
            <div className="font-semibold text-gray-900">{t("내부 팝업 URL")}</div>
            <div className="mt-1">{t("아바타 보상 이벤트 팝업을 열려면 이동 URL에")} <code className="px-1 py-0.5 rounded bg-white border border-yellow-200">/events/avatar-reward</code> {t("를 입력하세요.")}</div>
            <button
              type="button"
              className="mt-2 px-2.5 py-1.5 rounded border border-yellow-300 bg-white hover:bg-yellow-100 text-gray-900"
              onClick={() => setField('url', '/events/avatar-reward')}
            >
              {t("아바타 보상 이벤트 팝업 URL 적용")}
            </button>
          </div>
        </div>

        <div className="mt-4 border rounded p-3 bg-gray-50">
          <div className="font-medium mb-2">{t("\uD14D\uC2A4\uD2B8 \uC2A4\uD0C0\uC77C")}</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uD3F0\uD2B8\uC2A4\uD0C0\uC77C")}</span>
              <select className="border rounded px-3 py-2 bg-white" value={value.textFontFamily || INFO_TEXT_STYLE_DEFAULTS.textFontFamily} onChange={(e) => setField('textFontFamily', e.target.value)}>
                <option value="inherit">inherit</option>
                <option value="Pretendard">Pretendard</option>
                <option value="Arial">Arial</option>
                <option value="Noto Sans KR">Noto Sans KR</option>
                <option value="sans-serif">sans-serif</option>
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uAD75\uAE30")}</span>
              <select className="border rounded px-3 py-2 bg-white" value={String(value.textFontWeight || INFO_TEXT_STYLE_DEFAULTS.textFontWeight)} onChange={(e) => setField('textFontWeight', e.target.value)}>
                <option value="400">400</option>
                <option value="500">500</option>
                <option value="600">600</option>
                <option value="700">700</option>
                <option value="800">800</option>
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uC0C9\uC0C1")}</span>
              <input className="border rounded px-3 py-2 h-[42px] bg-white" type="color" value={value.textColor || INFO_TEXT_STYLE_DEFAULTS.textColor} onChange={(e) => setField('textColor', e.target.value)}/>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uD06C\uAE30(px)")}</span>
              <input className="border rounded px-3 py-2 bg-white" type="number" min="10" max="32" value={value.textFontSize ?? INFO_TEXT_STYLE_DEFAULTS.textFontSize} onChange={(e) => setField('textFontSize', e.target.value)}/>
            </label>
          </div>
          <div className="text-xs text-gray-500 mt-2">{t("* \uC774 \uC2A4\uD0C0\uC77C\uC740 \uC778\uD3EC\uCE74\uB4DC\uC758 text \uC694\uC18C\uC5D0\uB9CC \uC801\uC6A9\uB429\uB2C8\uB2E4. \uD0C0\uC774\uBA38/\uC544\uC774\uCF58\uC740 \uAE30\uC874 \uC2A4\uD0C0\uC77C\uC744 \uC720\uC9C0\uD569\uB2C8\uB2E4.")}</div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between">
            <div className="font-medium">{t("\uC694\uC18C(\uCD5C\uB300 4\uAC1C)")}</div>
            <div className="flex gap-2">
              <button type="button" disabled={!canAddElement} onClick={() => addElement('text')} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("+ \uD14D\uC2A4\uD2B8")}</button>
              <button type="button" disabled={!canAddElement} onClick={() => addElement('timer')} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("+ \uD0C0\uC774\uBA38(\uD45C\uC2DC\uC5EC\uBD80)")}</button>
              <button type="button" disabled={!canAddElement} onClick={() => addElement('icon')} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("+ \uC544\uC774\uCF58(\uC5C5\uB85C\uB4DC)")}</button>
            </div>
          </div>

          <div className="space-y-2 mt-2">
            {(value.elements || []).slice(0, 4).map((el, idx) => (<div key={idx} className="flex gap-2 items-center">
                <select className="border rounded px-2 py-2" value={el.type} onChange={(e) => setElement(idx, { type: e.target.value, value: e.target.value === 'timer' ? '' : (el.value || '') })}>
                  <option value="text">{t("\uD14D\uC2A4\uD2B8")}</option>
                  <option value="timer">{t("\uD0C0\uC774\uBA38(\uC874\uC7AC\uC720\uBB34\uB9CC)")}</option>
                  <option value="icon">{t("\uC544\uC774\uCF58(\uB85C\uCEEC \uC5C5\uB85C\uB4DC/URL)")}</option>
                </select>

                {/* [PATCH][ANCHOR:TIMER_NO_VALUE_INPUT] */}
                {el.type === 'timer' ? (<div className="flex-1 text-sm text-gray-600">{t("\uD0C0\uC774\uBA38\uB294")}<b>{t("\uD45C\uC2DC \uC5EC\uBD80\uB9CC")}</b>{t("\uC800\uC7A5\uB429\uB2C8\uB2E4. \uAE30\uC900 \uC2DC\uAC04\uC740 \uC704 \uC778\uD3EC\uCE74\uB4DC \uD0C0\uC774\uBA38 \uC124\uC815(timerMode/timerEndUtc)\uC744 \uC0AC\uC6A9\uD569\uB2C8\uB2E4.")}</div>) : el.type === 'icon' ? (<div className="flex-1 flex items-center gap-2">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => uploadIconFile(idx, e.target.files?.[0])} disabled={uploadingIdx === idx} className="text-sm"/>
                    <input className="border rounded px-3 py-2 flex-1" value={el.value || ''} onChange={(e) => setElement(idx, { value: e.target.value })} placeholder={t("\uC5C5\uB85C\uB4DC\uD558\uBA74 /uploads/... \uB85C \uC790\uB3D9 \uC785\uB825\uB428 (\uB610\uB294 URL \uC9C1\uC811 \uC785\uB825)")} disabled={uploadingIdx === idx}/>
                    {isLikelyUrl(el.value) ? (<img src={resolveAdminPreviewUrl(el.value)} alt="icon" className="w-8 h-8 rounded border object-contain bg-white"/>) : null}
                  </div>) : (<input className="border rounded px-3 py-2 flex-1" value={el.value || ''} onChange={(e) => setElement(idx, { value: e.target.value })} placeholder={t("\uD14D\uC2A4\uD2B8")}/>)}

                <button type="button" onClick={() => removeElement(idx)} className="px-2 py-2 rounded border hover:bg-gray-50">{t("\uC0AD\uC81C")}</button>
              </div>))}
          </div>

          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">{t("\uC11C\uBC84\uB85C \uC804\uC1A1\uB420 preset \uBB38\uC790\uC5F4(\uCF64\uB9C8 \uAD6C\uBD84)")}</div>
            <textarea readOnly className="w-full border rounded px-3 py-2 text-xs bg-gray-50" rows={2} value={presetPreview}/>
          </div>
        </div>
      </div>

      {enableAdvancedDesign ? <InfoCardDesignEditor value={value} onChange={onChange} /> : null}
    </div>);
}
